import React, { useState, memo } from 'react';
import { PipelineItem, DateRange, VisaClassType, CaseManagerType, PipelineType } from '../types';
import { filterDataByDateRange } from '../utils/dataUtils';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ExternalLink, PlusCircle, PenLine, CheckCircle } from 'lucide-react';
type EventFilter = 'all' | 'created' | 'drafted' | 'approved';
interface DataTableProps {
  data: PipelineItem[];
  dateRange: DateRange;
  pipelineType?: PipelineType;
}
const DataTable: React.FC<DataTableProps> = ({
  data,
  dateRange,
  pipelineType
}) => {
  const [visaClassFilter, setVisaClassFilter] = useState<VisaClassType | 'all'>('all');
  const [caseManagerFilter, setCaseManagerFilter] = useState<CaseManagerType | 'all'>('all');
  const [eventFilter, setEventFilter] = useState<EventFilter>('all');
  const [sortField, setSortField] = useState<keyof PipelineItem>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 20;
  // Get the appropriate heading based on pipeline type
  const getTableHeading = () => {
    if (!pipelineType) return 'Raw Data';
    const headings = {
      form: 'Forms Details',
      letter: 'Letters Details',
      memo: 'Memos Details'
    };
    return headings[pipelineType];
  };
  // Filter data based on selected filters
  const filteredData = filterDataByDateRange(data, dateRange).filter(item => {
    // Filter by visa class and case manager
    const matchesVisaClass = visaClassFilter === 'all' || item.visaClass === visaClassFilter;
    const matchesCaseManager = caseManagerFilter === 'all' || item.caseManager === caseManagerFilter;
    // Filter by event type in the current period
    let matchesEventFilter = true;
    if (eventFilter !== 'all') {
      switch (eventFilter) {
        case 'created':
          matchesEventFilter = item.createdAt >= dateRange.startDate && item.createdAt <= dateRange.endDate;
          break;
        case 'drafted':
          matchesEventFilter = item.firstDraftAt !== null && item.firstDraftAt >= dateRange.startDate && item.firstDraftAt <= dateRange.endDate;
          break;
        case 'approved':
          matchesEventFilter = item.approvalAt !== null && item.approvalAt >= dateRange.startDate && item.approvalAt <= dateRange.endDate;
          break;
      }
    }
    return matchesVisaClass && matchesCaseManager && matchesEventFilter;
  });
  const sortedData = [...filteredData].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (aValue === null || aValue === undefined) return sortDirection === 'asc' ? -1 : 1;
    if (bValue === null || bValue === undefined) return sortDirection === 'asc' ? 1 : -1;
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  // Calculate pagination
  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  // Ensure current page is within valid range
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(totalPages);
  }
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedData = sortedData.slice(startIndex, endIndex);
  const handleSort = (field: keyof PipelineItem) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };
  const formatDate = (date: Date | null) => {
    return date ? format(date, 'MMM dd, yyyy') : '-';
  };
  const calculateDuration = (start: Date | null, end: Date | null) => {
    if (!start || !end) return '-';
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  };
  const visaClasses: VisaClassType[] = ['O-1A', 'EB-1A', 'TN', 'H-1B', 'EB-2 NIW', 'O-1A RFE', 'O-1A Agent', 'E-3', 'AOS', 'J-1', 'EB-2 NIW RFE', 'EB-1A RFE'];
  const getVisaClassColor = (visaClass: VisaClassType): string => {
    const colors: Record<string, string> = {
      'O-1A': 'bg-blue-100 text-blue-800',
      'EB-1A': 'bg-green-100 text-green-800',
      TN: 'bg-purple-100 text-purple-800',
      'H-1B': 'bg-yellow-100 text-yellow-800',
      'EB-2 NIW': 'bg-indigo-100 text-indigo-800',
      'O-1A RFE': 'bg-blue-100 text-blue-800',
      'O-1A Agent': 'bg-blue-100 text-blue-800',
      'E-3': 'bg-pink-100 text-pink-800',
      AOS: 'bg-red-100 text-red-800',
      'J-1': 'bg-orange-100 text-orange-800',
      'EB-2 NIW RFE': 'bg-indigo-100 text-indigo-800',
      'EB-1A RFE': 'bg-green-100 text-green-800'
    };
    return colors[visaClass] || 'bg-gray-100 text-gray-800';
  };
  // Open builder in new tab
  const openBuilder = (itemId: string) => {
    window.open(`/builder/${itemId}`, '_blank');
  };
  // Pagination controls
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);
  // Event filter options with icons
  const eventFilterOptions = [{
    value: 'all',
    label: 'All Events',
    icon: null
  }, {
    value: 'created',
    label: 'Created',
    icon: PlusCircle
  }, {
    value: 'drafted',
    label: 'Drafted',
    icon: PenLine
  }, {
    value: 'approved',
    label: 'Approved',
    icon: CheckCircle
  }];
  return <div data-id="element-10">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4" data-id="element-11">
        <h2 className="text-xl font-semibold" data-id="element-12">{getTableHeading()}</h2>
        <div className="flex flex-wrap gap-3" data-id="element-13">
          {/* Event filter as dropdown */}
          <select className="border border-gray-300 rounded px-3 py-1.5 text-sm" value={eventFilter} onChange={e => {
          setEventFilter(e.target.value as EventFilter);
          setCurrentPage(1); // Reset to first page when filter changes
        }} data-id="element-14">
            {eventFilterOptions.map(option => <option key={option.value} value={option.value} data-id="element-15">
                {option.label}
              </option>)}
          </select>
          {/* Existing filters */}
          <select className="border border-gray-300 rounded px-3 py-1.5 text-sm" value={visaClassFilter} onChange={e => {
          setVisaClassFilter(e.target.value as VisaClassType | 'all');
          setCurrentPage(1); // Reset to first page when filter changes
        }} data-id="element-16">
            <option value="all" data-id="element-17">All Visa Classes</option>
            {visaClasses.map(visaClass => <option key={visaClass} value={visaClass} data-id="element-18">
                {visaClass}
              </option>)}
          </select>
          <select className="border border-gray-300 rounded px-3 py-1.5 text-sm" value={caseManagerFilter} onChange={e => {
          setCaseManagerFilter(e.target.value as CaseManagerType | 'all');
          setCurrentPage(1); // Reset to first page when filter changes
        }} data-id="element-19">
            <option value="all" data-id="element-20">All Case Managers</option>
            <option value="Robby" data-id="element-21">Robby</option>
            <option value="Louise" data-id="element-22">Louise</option>
          </select>
        </div>
      </div>
      <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-23">
        <div className="overflow-x-auto" data-id="element-24">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-25">
            <thead className="bg-gray-50" data-id="element-26">
              <tr data-id="element-27">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" data-id="element-28">
                  Beneficiary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-29">
                  Visa Class
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('caseManager')} data-id="element-30">
                  Case Manager{' '}
                  {sortField === 'caseManager' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('createdAt')} data-id="element-31">
                  Created{' '}
                  {sortField === 'createdAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('firstDraftAt')} data-id="element-32">
                  First Draft{' '}
                  {sortField === 'firstDraftAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('approvalAt')} data-id="element-33">
                  Approval{' '}
                  {sortField === 'approvalAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-34">
                  Draft Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-35">
                  Approval Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-36">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-37">
              {paginatedData.map(item => <tr key={item.id} data-id="element-38">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-39">
                    <div className="font-medium text-gray-900" data-id="element-40">
                      {item.beneficiary}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-41">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getVisaClassColor(item.visaClass)}`} data-id="element-42">
                      {item.visaClass}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-43">
                    {item.caseManager}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-44">
                    {formatDate(item.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-45">
                    {formatDate(item.firstDraftAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-46">
                    {formatDate(item.approvalAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-47">
                    {calculateDuration(item.createdAt, item.firstDraftAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-48">
                    {calculateDuration(item.firstDraftAt, item.approvalAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-49">
                    <button onClick={() => openBuilder(item.id)} className="inline-flex items-center px-3 py-1.5 border border-blue-600 text-blue-600 bg-white hover:bg-blue-50 rounded text-sm font-medium" data-id="element-50">
                      Builder
                      <ExternalLink className="ml-1 h-3.5 w-3.5" data-id="element-51" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 bg-gray-50 flex items-center justify-between" data-id="element-52">
          <p className="text-sm text-gray-700" data-id="element-53">
            Showing <span className="font-medium" data-id="element-54">{startIndex + 1}</span> to{' '}
            <span className="font-medium" data-id="element-55">{endIndex}</span> of{' '}
            <span className="font-medium" data-id="element-56">{totalItems}</span> items
          </p>
          <div className="flex items-center space-x-2" data-id="element-57">
            <button onClick={goToFirstPage} disabled={currentPage === 1} className="p-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="First page" data-id="element-58">
              <ChevronsLeft className="h-4 w-4" data-id="element-59" />
            </button>
            <button onClick={goToPreviousPage} disabled={currentPage === 1} className="p-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Previous page" data-id="element-60">
              <ChevronLeft className="h-4 w-4" data-id="element-61" />
            </button>
            <span className="text-sm text-gray-700" data-id="element-62">
              Page <span className="font-medium" data-id="element-63">{currentPage}</span> of{' '}
              <span className="font-medium" data-id="element-64">{totalPages}</span>
            </span>
            <button onClick={goToNextPage} disabled={currentPage === totalPages} className="p-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Next page" data-id="element-65">
              <ChevronRight className="h-4 w-4" data-id="element-66" />
            </button>
            <button onClick={goToLastPage} disabled={currentPage === totalPages} className="p-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" aria-label="Last page" data-id="element-67">
              <ChevronsRight className="h-4 w-4" data-id="element-68" />
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export default DataTable;